// The one component that is AIW-specific: a rendered backbone case (the six
// questions) with a verdict row, in the signed language (S-D 2026-09-07).
// Every face shows a case the same way, so the shape lives here rather than in
// each app. Consumers render and post a verdict; they never execute (ADR-003).
//
// Anatomy, in this order: the question · what will happen · why now · options
// (EmmA's pick is "EmmA suggests", the person's choice is the radio) · what I
// used · who it touches · if you say no + undo words · verbs · thread.
// No radius numbers, ids, command or table names on the card.
import * as React from "react";
import {translate,preferredLang,type Lang} from "@aiw/i18n";
import type { Case, Verdict } from "@artemis-group/aiw-contracts";
import { Badge } from "./badge";
import { Button } from "./button";
import { Textarea } from "./textarea";
import { caseState } from "../lib/case-state";
import { cn } from "../lib/cn";
import { aud, caseHeadline, caseKind, isExternalCase, confidenceWords, deskName, undoWords } from "../lib/case-words";

export type CaseUiState =
  | "proposed"
  | "revision requested"
  | "revised"
  | "superseded"
  | "approved"
  | "executing"
  | "succeeded"
  | "needs checking"
  | "failed"
  | "declined"
  | "expired";

export interface CaseCardProps {
  kase: Case;
  onDecide: (decision: Verdict["decision"], extra?: { reason?: string | null }) => void | Promise<void>;
  busy?: boolean;
  /** full = job card / PO desk; inline = Today and lists (collapsed detail). */
  size?: "full" | "inline";
  /** local state overlay once a verdict has been posted */
  state?: CaseUiState;
  /** where the work is: a link the inline card offers ("Open job J0926…") */
  context?: { label: string; href: string };
  className?: string;
  lang?: Lang;
}

const STATE_TONE: Record<CaseUiState, "secondary" | "warning" | "success" | "destructive" | "outline" | "default"> = {
  proposed: "secondary",
  "revision requested": "warning",
  revised: "secondary",
  superseded: "outline",
  approved: "success",
  executing: "default",
  succeeded: "success",
  failed: "destructive",
  "needs checking": "warning",
  declined: "outline",
  expired: "outline",
};

const KIND_TONE: Record<string, string> = {
  money: "border-gold/40 bg-gold/10 text-gold",
  "a note": "border-border bg-muted text-muted-foreground",
  "our records": "border-primary/30 bg-accent text-accent-foreground",
};

function timeOf(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", timeZone:"Australia/Sydney" });
}

export function KindChip({ kind, className }: { kind: string; className?: string }) {
  const tone = KIND_TONE[kind] ?? "border-warning/40 bg-warning/10 text-warning";
  return <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-micro font-medium leading-4", tone, className)}>{kind}</span>;
}

export function CaseCard({ kase, onDecide, busy, size = "full", state, context, className, lang=preferredLang(globalThis.document?.documentElement.lang??'en') }: CaseCardProps) {
  const q = kase.questions;
  const kind = caseKind(kase);
  const external = isExternalCase(kase);
  const communication = kase.command === 'send_communication';
  const historyOnly = kase.command === "add_touch";
  const missingRecipient = external && q.touches.recipients.length === 0 && !(q.what.kind === "email" && q.what.to.length > 0);
  const uiState: CaseUiState = state ?? caseState(kase);
  const settled = uiState !== "proposed" && uiState !== "revised";
  // ADR-027: only what EmmA raises is "EmmA asks". A staff member's own action is theirs, and once a case is
  // settled nothing on it is waiting for approval (Jacky, 5 October 2026: "my own actions are my approval").
  const fromEmma = kase.proposed_by.kind !== "human";
  const who = translate(lang, fromEmma ? 'case.emmaAsks' : 'case.yourAction');
  const [mode, setMode] = React.useState<"idle" | "change" | "decline">("idle");
  const [reason, setReason] = React.useState("");
  const [open, setOpen] = React.useState(size === "full");
  const headline = caseHeadline(kase);
  const suggested = communication ? communicationApprovalLabel(kase,lang) : optionLabel(kase);

  return (
    <article
      className={cn("rounded-lg border border-l-4 border-l-rail bg-card text-card-foreground", className)}
      aria-label={`${who}: ${headline}`}
      data-case-state={uiState}
    >
      <header className="flex flex-wrap items-center gap-2 px-4 pt-3 text-micro text-muted-foreground">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-rail font-display text-micro font-semibold leading-none text-rail-foreground" aria-hidden>E</span>
        <span className="font-medium text-foreground">{who}</span>
        {!communication && <span>· {deskName(kase.proposed_by.id)}</span>}
        {kase.created_at && <span>· {timeOf(kase.created_at)}</span>}
        {/* The request number Jacky reads (4 Nov 2026): the same # follows a draft from its card to its send card. */}
        {kase.request_no != null && <span className="font-mono font-semibold text-foreground" data-request-no={kase.request_no}>#{kase.request_no}</span>}
        <span className="ml-auto flex items-center gap-2">
          <KindChip kind={kind} />
          <Badge variant={STATE_TONE[uiState]} className="capitalize">{uiState}</Badge>
        </span>
      </header>

      <div className="px-4 pb-4 pt-2">
        <h3 className="font-display text-[1.2rem] font-medium leading-snug text-foreground">{headline}</h3>

        {historyOnly && <div className="mt-3 rounded-md border bg-accent/40 p-3 text-sm"><p className="font-medium">Internal history only — nothing will be sent.</p><p>This records an activity that already happened. It sends no message, calendar invitation or notification to the customer or installer.</p></div>}

        {external && !communication && !settled && <div className="mt-3 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"><p className="font-semibold">Approval authorises outgoing communication or an external action.</p><p>Review the recipient, full content and context below before approving.</p>{missingRecipient && <p role="alert">Recipient is missing. Request changes before approval.</p>}</div>}

        {size === "inline" && !open && !external && (
          <p className="mt-1 text-sm text-muted-foreground">{q.why_now.trigger}</p>
        )}

        {(open || external) && (
          <div className="mt-3 space-y-3 text-sm">
            {kase.delivery_status && <p role="status" className="text-sm">{kase.delivery_status==='queued'?'Approved for delivery — not sent yet.':kase.delivery_status==='confirmed'?'Sending confirmed in the mailbox. Supplier receipt is not confirmed.':kase.delivery_status==='uncertain'?'Sending outcome needs checking. Do not resend this order.':kase.delivery_status==='failed'?'Delivery failed before sending.':kase.delivery_status==='cancelled'?'Delivery cancelled.':'Delivery is in progress — sending is not confirmed yet.'}{kase.delivery_error?` ${kase.delivery_error}`:''}</p>}
      {communication && <p role="status">{translate(lang,settled?'case.checkStatus':'case.awaiting')}</p>}
      {communication && missingRecipient && <p role="alert">{translate(lang,'case.missingRecipient')}</p>}
      <Section label={communication ? translate(lang,'case.message') : external ? "Full content / action to approve" : "What will happen"}>
              <Artefact what={historyOnly && q.what.kind === "text" ? {kind: "text", body: caseHeadline(kase)} : q.what} />
            </Section>
            <details open={communication ? undefined : true}><summary className="cursor-pointer text-muted-foreground">{translate(lang,'case.context')}</summary>
            <Section label="Why now">
              <p>{q.why_now.trigger}</p>
            </Section>
            <Section label="Options">
              <ul className="space-y-1.5">
                <li className="flex items-start gap-2 rounded-md border border-primary/40 bg-accent/40 px-3 py-2">
                  <span className="mt-1 h-3 w-3 shrink-0 rounded-full border-4 border-primary bg-background" aria-hidden />
                  <span className="flex-1">
                    <span className="font-medium">{suggested}</span>
                    <span className="block text-muted-foreground">{q.considered.reasoning}</span>
                  </span>
                  <span className="shrink-0 rounded bg-rail px-1.5 py-0.5 text-micro font-medium text-rail-foreground">EmmA suggests</span>
                </li>
                {q.considered.rejected.map((r) => (
                  <li key={r} className="flex items-start gap-2 px-3 py-1.5 text-muted-foreground">
                    <span className="mt-1 h-3 w-3 shrink-0 rounded-full border border-border" aria-hidden />
                    <span>{r} <span className="text-micro">· not suggested</span></span>
                  </li>
                ))}
              </ul>
            </Section>
            <Section label="What I used">
              <p className="font-mono text-micro leading-5 text-muted-foreground">
                {q.why_now.trigger} · EmmA is {confidenceWords(q.considered.confidence)} ({Math.round(q.considered.confidence * 100)}%)
              </p>
            </Section>
            <Section label="Who it touches">
              <p>
                {q.touches.recipients.length ? q.touches.recipients.join(", ") : "nobody outside Artemis"}
                {q.touches.amount_aud != null && <span className="ml-2 font-mono text-gold">{aud(q.touches.amount_aud)}</span>}
              </p>
            </Section>
            <div className="grid gap-3 sm:grid-cols-2">
              <Section label="If you say no"><p>{q.if_no}</p></Section>
              <Section label="Undo"><p>{undoWords(q.undo.kind)}</p></Section>
            </div>
            </details>
          </div>
        )}

        {!settled && mode !== "idle" && (
          <div className="mt-3 space-y-2 rounded-md border bg-background p-3 text-sm">
            <label className="block font-medium" htmlFor={`reason-${kase.id}`}>
              {mode === "change" ? "What should change?" : "Why decline? (optional)"}
            </label>
            <Textarea id={`reason-${kase.id}`} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={4000} placeholder={mode === "change" ? "Describe the changes needed before a fresh review." : "A sentence for the record."} />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={mode === "change" ? "default" : "destructive"}
                disabled={busy || (mode === "change" && reason.trim().length < 3)}
                onClick={() => void onDecide(mode === "change" ? "request_revision" : "reject", { reason: reason.trim() || null })}
              >
                {mode === "change" ? "Request changes" : "Decline"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setMode("idle")}>Back</Button>
            </div>
          </div>
        )}

        {kase.revision_request && <div className="mt-3 rounded-md border bg-background p-3 text-sm"><p className="font-medium">Requested changes</p><p className="whitespace-pre-wrap">{kase.revision_request.reason}</p><p className="mt-1 text-muted-foreground">{kase.replacement_case_id ? "A replacement proposal has been submitted for fresh review." : "The original proposal cannot proceed. A revised proposal needs a fresh approval."}</p></div>}

        <footer className="mt-3 flex flex-wrap items-center gap-2">
          {!settled ? (
            <>
              <Button size="sm" disabled={busy || mode !== "idle" || missingRecipient} onClick={() => void onDecide("approve")}>
                {kase.command==='send_communication'?communicationApprovalLabel(kase,lang):<>Approve: {suggested}</>}
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setMode("change")}>Change…</Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setMode("decline")}>Decline</Button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              {uiState === "declined" ? "Declined" : uiState === "revision requested" ? "Changes recorded — awaiting a revised proposal" : `${capitalise(uiState)}`}
              {settled && q.undo.kind !== "irreversible" && uiState !== "declined" && uiState !== "revision requested" && <span> · {undoWords(q.undo.kind)}</span>}
            </p>
          )}
          <span className="ml-auto flex items-center gap-3 text-micro">
            {context && <a className="text-primary underline-offset-2 hover:underline" href={context.href}>{context.label}</a>}
            {size === "inline" && !external && (
              <button type="button" className="text-muted-foreground underline-offset-2 hover:underline" onClick={() => setOpen((o) => !o)}>
                {open ? "Less" : "Details"}
              </button>
            )}
          </span>
        </footer>
      </div>
    </article>
  );
}

function capitalise(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Channel comes from the server-rendered review header, never words inside the message body. */
export function communicationApprovalLabel(k:Case,lang:Lang='en'):string{
 const what=k.questions.what;
 const header=what.kind==='text'?what.body.split('\n',1)[0]??'':'';
 // The send card opens "Text <name> · …" / "Email <name> · …" since 12 Oct 2026 (sendCardWords); older cards "Text to …".
 return translate(lang,/^Text\b/.test(header)?'case.approveSms':what.kind==='email'||/^Email\b/.test(header)?'case.approveEmail':'case.approveMessage');
}

/** The suggested option, in words, for the Approve button. */
function optionLabel(k: Case): string {
  if (k.command === "add_touch") return "record past activity";
  if (k.command === "save_communication") return "save draft only";
  if (k.command === "send_email") return "send this email";
  if (k.command === "send_sms") return "send this SMS";
  if (isExternalCase(k)) return k.questions.what.kind === "email" ? "send this email" : "carry out this external action";
  if (k.command === 'create_job') return k.questions.what.kind==='record' && k.questions.what.fields.existing_job ? 'return the original job' : 'create the accepted job';
  if(k.command==='reopen_job')return 'restart the same work';
  const w = k.questions.what;
  switch (w.kind) {
    case "email":
      return `send the email`;
    case "record":
      return `record it`;
    default:
      return "do it as drafted";
  }
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-1 font-mono text-micro uppercase tracking-wide text-muted-foreground">{label}</div>
      {children}
    </section>
  );
}

function Artefact({ what }: { what: Case["questions"]["what"] }) {
  switch (what.kind) {
    case "email":
      return (
        <div className="rounded-md border bg-background p-3">
          <div className="text-micro text-muted-foreground">To {what.to.join(", ")}</div>
          <div className="font-medium">{what.subject}</div>
          <div className="prose prose-sm mt-1 max-w-none" dangerouslySetInnerHTML={{ __html: what.body_html }} />
        </div>
      );
    case "html":
      return <div className="rounded-md border bg-background p-3" dangerouslySetInnerHTML={{ __html: what.body }} />;
    case "record":
      return (
        <div className="rounded-md border bg-background p-3">
          <dl className="grid grid-cols-[minmax(5rem,auto)_minmax(0,1fr)] gap-x-3 gap-y-0.5">
            {Object.entries(what.fields).filter(([k])=>!/(^id$|_id$|_ref$|^expected_)/.test(k)).map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="text-muted-foreground">{k.replace(/_/g, " ")}</dt>
                <dd className="break-words text-sm leading-5">{String(v)}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      );
    default:
      return <pre className="whitespace-pre-wrap rounded-md border bg-background p-3 font-sans">{what.body}</pre>;
  }
}
